/**
 * The timeline of one chat turn: when each model step began and each tool call
 * started and ended, as the server saw them. Stored with the answer, it is
 * what the work card times its rows from — live and after a reload alike.
 *
 * Shared because the UI reads it back off a persisted message through a cast:
 * a field renamed on the API alone still compiles, and the card shows no time.
 */

/**
 * The part carrying the timeline, one per answer. Stored whole; on the wire it
 * goes out once, empty, and each event follows in its own part.
 */
export const CHAT_TRACE_TIMELINE_PART_TYPE = "data-traceTimeline";

/**
 * One event of the timeline on the wire, each with its own id so the parts
 * add up: resending the whole timeline on every event grew with the square of
 * the turn. Never stored — the stored answer carries the whole timeline.
 */
export const CHAT_TRACE_EVENT_PART_TYPE = "data-traceEvent";

export type ChatTraceTimelineEvent =
  /** The model opened a step: it thinks before writing or calling. */
  | { readonly type: "step"; readonly at: number }
  | { readonly type: "toolStart"; readonly toolCallId: string; readonly at: number }
  /** Settled: an output, an error, a refusal or a malformed input. */
  | { readonly type: "toolEnd"; readonly toolCallId: string; readonly at: number }
  /**
   * The model started writing text after a call — or after nothing: maybe a
   * line announcing the next call, maybe the answer.
   */
  | { readonly type: "text"; readonly at: number }
  /** What it writes is the answer itself: the card's work is over. */
  | { readonly type: "answer"; readonly at: number };

export type ChatTraceTimelineData = {
  /** When the turn started, in epoch milliseconds on the server's clock. */
  readonly startedAt: number;
  readonly events: readonly ChatTraceTimelineEvent[];
};
